import { Avatar, AvatarFallback, Button, Card, Input, cn } from '@repo/ui'
import { createFileRoute } from '@tanstack/react-router'
import {
  Circle,
  Download,
  MessageSquare,
  Paperclip,
  Search,
  Send,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { Driver } from '@/lib/types/drvier'
import type { Message, MessageAttachment } from '@/lib/types/types'
import type React from 'react'

import { useDrivers } from '@/hooks/api-hooks'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { formatRelativeTime } from '@/lib/format'
import { mockMessages } from '@/lib/mock-data'
import { localizedPageTitle } from '@/lib/page-title'

export const Route = createFileRoute('/dashboard/messages/')({
  head: () => ({ meta: [{ title: localizedPageTitle('messages') }] }),
  component: MessagesPage,
})

const filter = (messages: Array<Message>, selectedDriverId: string) => {
  return messages.filter(
    (msg) =>
      (msg.senderId === selectedDriverId && msg.receiverId === 'admin') ||
      (msg.senderId === 'admin' && msg.receiverId === selectedDriverId),
  )
}

export default function MessagesPage() {
  useDocumentTitle('messages')
  const { data: drivers = [] } = useDrivers()
  const { t } = useTranslation('messages')
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null)
  const [messages, setMessages] = useState<Array<Message>>(mockMessages)
  const [newMessage, setNewMessage] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const selectedDriver = drivers.find((d) => d.id === Number(selectedDriverId))

  const conversationMessages = filter(messages, selectedDriverId as string)

  const conversations = drivers
    .map((driver) => {
      const driverMessages = filter(messages, driver.id.toString())
      const lastMessage = driverMessages[driverMessages.length - 1]
      const unreadCount = driverMessages.filter(
        (msg) => !msg.read && msg.senderId === driver.id.toString(),
      ).length

      return {
        driver,
        lastMessage,
        unreadCount,
      }
    })
    .filter((conv): conv is typeof conv & { lastMessage: Message } =>
      Boolean(conv.lastMessage),
    )
    .filter((conv) =>
      conv.driver.name?.toLowerCase().includes(searchQuery.toLowerCase()),
    )
    .sort(
      (a, b) =>
        new Date(b.lastMessage.timestamp).getTime() -
        new Date(a.lastMessage.timestamp).getTime(),
    )

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [conversationMessages])

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !selectedDriverId) return

    const attachment: MessageAttachment = {
      id: `att-${Date.now()}`,
      name: file.name,
      size: file.size,
      type: file.type,
      url: URL.createObjectURL(file),
    }

    const message: Message = {
      id: `msg-${Date.now()}`,
      senderId: 'admin',
      receiverId: selectedDriverId,
      content: '',
      timestamp: new Date().toISOString(),
      read: false,
      attachment,
    }

    setMessages([...messages, message])

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const getFileIcon = (type: string) => {
    if (type.includes('pdf')) return '📄'
    if (type.includes('image')) return '🖼️'
    if (type.includes('video')) return '🎥'
    if (type.includes('word') || type.includes('document')) return '📝'
    if (type.includes('excel') || type.includes('spreadsheet')) return '📊'
    return '📎'
  }

  const handleSendMessage = () => {
    if (!newMessage.trim() || !selectedDriverId) return

    const message: Message = {
      id: `msg-${Date.now()}`,
      senderId: 'admin',
      receiverId: selectedDriverId,
      content: newMessage,
      timestamp: new Date().toISOString(),
      read: false,
    }

    setMessages([...messages, message])
    setNewMessage('')
  }

  const formatTime = (timestamp: string) =>
    formatRelativeTime(timestamp, t('yesterday'))

  const getDriverStatus = (driver: Driver) => {
    return driver.status === 'driving' || driver.status === 'available'
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">{t('title')}</h1>
        <p className="text-muted-foreground">{t('subtitle')}</p>
      </div>

      <div className="grid h-[calc(100vh-12rem)] grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="flex flex-col overflow-hidden lg:col-span-1">
          <div className="border-b p-4">
            <div className="relative">
              <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder={t('searchDrivers')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center p-6 text-center">
                <p className="text-muted-foreground">{t('noConversations')}</p>
              </div>
            ) : (
              conversations.map(({ driver, lastMessage, unreadCount }) => (
                <button
                  key={driver.id}
                  onClick={() => setSelectedDriverId(driver.id.toString())}
                  className={cn(
                    'hover:bg-muted/50 flex w-full items-center gap-3 border-b p-4 transition-colors',
                    selectedDriverId === driver.id.toString() && 'bg-muted',
                  )}
                >
                  <div className="relative">
                    <Avatar className="h-12 w-12">
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {driver.name
                          ?.split(' ')
                          .map((n) => n[0])
                          .join('')}
                      </AvatarFallback>
                    </Avatar>
                    <Circle
                      className={cn(
                        'border-background absolute right-0 bottom-0 h-3 w-3 rounded-full border-2',
                        getDriverStatus(driver as any)
                          ? 'fill-green-500 text-green-500'
                          : 'fill-gray-400 text-gray-400',
                      )}
                    />
                  </div>
                  <div className="min-w-0 flex-1 text-left">
                    <div className="mb-1 flex items-center justify-between">
                      <p className="truncate font-semibold">{driver.name}</p>
                      <span className="text-muted-foreground text-xs">
                        {formatTime(lastMessage.timestamp)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-muted-foreground truncate text-sm">
                        {lastMessage.senderId === 'admin' ? 'You: ' : ''}
                        {lastMessage.attachment ? (
                          <span className="flex items-center gap-1">
                            <Paperclip className="h-3 w-3" />
                            {lastMessage.attachment.name}
                          </span>
                        ) : (
                          lastMessage.content
                        )}
                      </p>
                      {unreadCount > 0 && (
                        <span className="bg-primary text-primary-foreground ml-2 flex h-5 w-5 items-center justify-center rounded-full text-xs">
                          {unreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </Card>

        <Card className="flex flex-col overflow-hidden lg:col-span-2">
          {selectedDriver ? (
            <>
              <div className="flex items-center gap-3 border-b p-4">
                <div className="relative">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {selectedDriver.name
                        ?.split(' ')
                        .map((n) => n[0])
                        .join('')}
                    </AvatarFallback>
                  </Avatar>
                  <Circle
                    className={cn(
                      'border-background absolute right-0 bottom-0 h-3 w-3 rounded-full border-2',
                      getDriverStatus(selectedDriver as any)
                        ? 'fill-green-500 text-green-500'
                        : 'fill-gray-400 text-gray-400',
                    )}
                  />
                </div>
                <div>
                  <p className="font-semibold">{selectedDriver.name}</p>
                  <p className="text-muted-foreground text-xs">
                    {getDriverStatus(selectedDriver as any)
                      ? t('online')
                      : t('offline')}
                  </p>
                </div>
              </div>

              <div className="flex-1 space-y-4 overflow-y-auto p-4">
                {conversationMessages.map((message) => {
                  const isOwnMessage = message.senderId === 'admin'
                  return (
                    <div
                      key={message.id}
                      className={cn(
                        'flex',
                        isOwnMessage ? 'justify-end' : 'justify-start',
                      )}
                    >
                      <div
                        className={cn(
                          'max-w-[70%] rounded-2xl px-4 py-2',
                          isOwnMessage
                            ? 'bg-primary text-primary-foreground rounded-br-sm'
                            : 'bg-muted rounded-bl-sm',
                        )}
                      >
                        {message.attachment ? (
                          <div className="space-y-2">
                            <div
                              className={cn(
                                'flex items-center gap-3 rounded-lg border p-3',
                                isOwnMessage
                                  ? 'border-primary-foreground/20 bg-primary-foreground/10'
                                  : 'border-border bg-background',
                              )}
                            >
                              <div className="text-2xl">
                                {getFileIcon(message.attachment.type)}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium">
                                  {message.attachment.name}
                                </p>
                                <p
                                  className={cn(
                                    'text-xs',
                                    isOwnMessage
                                      ? 'text-primary-foreground/70'
                                      : 'text-muted-foreground',
                                  )}
                                >
                                  {formatFileSize(message.attachment.size)}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                variant={isOwnMessage ? 'secondary' : 'outline'}
                                className="h-8 w-8 p-0"
                                onClick={() => {
                                  const link = document.createElement('a')
                                  link.href = message.attachment!.url
                                  link.download = message.attachment!.name
                                  link.click()
                                }}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </div>
                            {message.content && (
                              <p className="text-sm">{message.content}</p>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm">{message.content}</p>
                        )}
                        <p
                          className={cn(
                            'mt-1 text-xs',
                            isOwnMessage
                              ? 'text-primary-foreground/70'
                              : 'text-muted-foreground',
                          )}
                        >
                          {formatTime(message.timestamp)}
                        </p>
                      </div>
                    </div>
                  )
                })}
                <div ref={messagesEndRef} />
              </div>

              <div className="border-t p-4">
                <div className="flex gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileSelect}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.mp4,.mov"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => fileInputRef.current?.click()}
                    title={t('attachDocument')}
                  >
                    <Paperclip className="h-4 w-4" />
                  </Button>
                  <Input
                    placeholder={t('typeMessage')}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        handleSendMessage()
                      }
                    }}
                    className="flex-1"
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim()}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center p-6 text-center">
              <div>
                <MessageSquare className="text-muted-foreground mx-auto mb-4 h-16 w-16" />
                <p className="mb-2 text-lg font-semibold">
                  {t('selectDriver')}
                </p>
                <p className="text-muted-foreground text-sm">{t('subtitle')}</p>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}
